import { ExceptionService } from '../services/exception.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Component, Input, Output, EventEmitter, OnInit, AfterContentChecked, ViewChild } from '@angular/core';
import { TogetherAuctionDIPLoanDetails } from './../models/api.models';
import { TogetherDIPStageContainer, TogetherDIPState } from './../models/app.models';
import { NodeAPIService } from './../services/node.api.service';
import { NodeAPIEntityService } from './../services/node.entity.service';
import { TogetherAuctionDIPEntityType } from './../models/enums';
import * as msg from './../conf/messages';
import * as _ from 'lodash-es';
import numeral from 'numeral';
import { DipStateService } from '../services/dip-state-service.service';
import { environment } from '../../environments/environment';
import { RecaptchaComponent } from 'ng-recaptcha';

@Component({
    selector: 'together-auction-dip-loan-details',
    templateUrl: './dip.loan_details.component.html',
    providers: [NodeAPIService, NodeAPIEntityService],
    standalone: false
})
export class TogetherAuctionDIPLoanDetailComponent implements OnInit {


  /**
   * Object containing results from API.
   *
   * @type {TogetherAuctionDIPLoanDetails}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  loanDetails: TogetherAuctionDIPLoanDetails = new TogetherAuctionDIPLoanDetails();

  /**
   * Loan details form which loan details elements are bound to.
   *
   * @type {FormGroup}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  tadLoanDetailsForm: UntypedFormGroup;

  @Input() is_dip: boolean = true;

  /**
   * Incoming stage from the DIP component.
   *
   * @type {TogetherDIPStageContainer}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  @Input() stage: TogetherDIPStageContainer;


  /**
   * Incoming property type from apply stage, if other type of property is selected.
   *
   * @type {string}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  @Input() property_type: string = '0';

  /**
   * Incoming commerciality flag (Deprecate before release.)
   *
   * @type {string}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  @Input() commerciality: string = '0';

  /**
   * The incoming case ID as captured in DIP component.
   *
   * @type {string}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  @Input() incoming_case_id: string;

  /**
   * Output signalling to DIP component that stage is changing, and to what.
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  @Output() stage_changing = new EventEmitter();

  /**
   * Unique, loan-detail only output to DIP component to update loan ID.
   *
   * @type {EventEmitter<string>}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  @Output() outgoing_case_id: EventEmitter<string> = new EventEmitter<string>();


  /**
   * Getter target for minLoanAmountFormatted.
   *
   * @type {number}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  minLoanAmount: number = 26000;

  /**
   * Getter target for maxLoanAmountFormatted.
   *
   * @type {number}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  maxLoanAmount: number = 49999999;

  /**
   * State variable to show loading component.
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  isComponentLoading: boolean = false;

  /**
   * Getter to show formatted min loan amount.
   *
   * @readonly
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  get minLoanAmountFormatted() {
    return numeral(this.minLoanAmount).format('0,000');
  }

  /**
   * Getter to show max
   *
   * @readonly
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  get maxLoanAmountFormatted() {
    return numeral(this.maxLoanAmount + 1).format('0,000');
  }

  /**
   * Property value form control.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  tadPropertyValue: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.min(0),
  ]);

  /**
   * Loan amount form control.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  tadLoanAmount: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.min(this.minLoanAmount),
    Validators.max(this.maxLoanAmount),
  ]);


  tadIsDip: UntypedFormControl = new UntypedFormControl(null, []);

  /**
   * Handles moving forward.
   *
   * @param {any} forward
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  onForwardClicked(forward) {
    if (!this.disableButtons()) {
      this.saveLoanDetails(
        this.incoming_case_id,
        this.loanDetails.CaseId,
        this.loanDetails,
      );
    }
  }
  /**
   * Reference to the RecaptchaComponent on the template
   *
   * @type {RecaptchaComponent}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;
  /**
   * Is ReCaptcha enabled for this environment
   *
   * @readonly
   * @type {boolean}
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  get isCaptchaEnabled(): boolean {
    return environment.captchaEnabled;
  }
  /**
   * Either call captcha if enabled
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  doTryForwards(): void {
    if (this.isCaptchaEnabled) {
      this.captchaRef.execute();
    } else {
      this.onForwardClicked(null);
    }
  }

  /**
   * Handles move backward.
   *
   * @param {any} backward
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  onBackwardClicked(backward) {
    this.stage_changing.emit(this.stage.backward_stage.reference);
  }

  /**
   * Validator for loan value
   *
   * @param {string} loan_amount
   * @param {string} property_value
   * @returns
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  loanValueValid(loanAmount: string, propertyValue: string) {
    return (group: UntypedFormGroup): { [key: string]: any } => {
      const la = group.controls[loanAmount].value;
      const pv = group.controls[propertyValue].value;

      return la <= pv ? null : {
        loanValueLess: true,
      };
    };
  }

  /**
   * Binds values after the user has input them.
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  ngAfterContentChecked() {
    this.bindFormToAPI();
  }

  /**
   * Initialises the component.
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  ngOnInit() {
    this.init();
  }

  /**
   * Handles binding form data to API outgoing request object.
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  bindFormToAPI() {
    if (!_.isNil(this.loanDetails)) {
      this.loanDetails.LoanAmount = this.tadLoanAmount.value;
      this.loanDetails.PropertyValuation = this.tadPropertyValue.value;
      this.loanDetails.IsDipJourney = _.isNil(this.tadIsDip) ? true : this.tadIsDip.value;
    }
  }

  /**
   * Disables buttons on the form when saving or loading.
   *
   * @returns
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  disableButtons() {
    return this.isComponentLoading || this.tadLoanDetailsForm.invalid === true;
  }

  /**
   * Binds the data from the init call to the object.
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  bindAPIDataToForm() {
    this.tadLoanAmount.setValue(this.loanDetails.LoanAmount);
    this.tadPropertyValue.setValue(this.loanDetails.PropertyValuation);
    const dip = _.isNil(this.is_dip) === false ? this.is_dip : this.loanDetails.IsDipJourney;
    this.tadIsDip.setValue(dip);
  }

  /**
   * Handles the saving of the object.
   *
   * @param {any} case_id
   * @param {any} entity_id
   * @param {any} entity
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  saveLoanDetails(caseId, entityId, entity) {
    this.isComponentLoading = true;
    this.entity.saveEntities(
      TogetherAuctionDIPEntityType.LoanDetails,
      caseId,
      entityId,
      entity,
    ).subscribe(
      (success) => {
        var response = new TogetherAuctionDIPLoanDetails(success as Object);
        if (response.IsOutSideCriteria) {
          alert("No product matching current criteria.");
          this.isComponentLoading = false;
        }
        else {
          // Set loan details object.
          this.loanDetails = new TogetherAuctionDIPLoanDetails(success as Object);

          // Before doing anything else let's get state in order
          this.state.caseId.next(this.loanDetails.CaseId);

          //  The following shouldn't change to just to be on the safe side for now...
          this.state.isDipJourney.next(this.loanDetails.IsDipJourney);
          this.state.propertyType.next(_.toInteger(this.loanDetails.PropertyType));

          // Bind data to form
          this.bindAPIDataToForm();

          // No longer loading.
          this.isComponentLoading = false;

          // Update outputs (TODO: Replace with service.)
          this.outgoing_case_id.emit(this.loanDetails.CaseId);
          this.stage_changing.emit(this.stage.forward_stage.reference);
        }
      },
      (error) => {
        this.exception.haveReceivedError(error);
        this.isComponentLoading = false;
      },
    );
  }

  /**
   * Prepares the data which is passed in outgoing emits.
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  setLoanDetailParams() {
    if (_.isNil(this.property_type) || _.isNil(this.is_dip)) {
      this.loanDetails.PropertyType = '0';
      this.loanDetails.IsDipJourney = true;
    } else {
      this.loanDetails.PropertyType = this.property_type;
      this.loanDetails.IsDipJourney = this.is_dip;
    }
    this.loanDetails.SalesPerson = this.state.SalesPerson;
    this.loanDetails.LeadReferenceNumber = this.state.SalesReference;
    this.tadIsDip.setValue(this.is_dip);
  }

  /**
   * Retrieves the data for the case from the API.
   *
   * @param {any} case_id
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  getLoanDetails(caseId) {
    this.isComponentLoading = true;
    this.entity.getAllEntities(TogetherAuctionDIPEntityType.LoanDetails, caseId)
      .subscribe(
        (success) => {
          this.loanDetails = new TogetherAuctionDIPLoanDetails(success as Object);
          this.bindAPIDataToForm();
          this.setLoanDetailParams();
          this.isComponentLoading = false;
        },
        (error) => {
          this.exception.haveReceivedError(error);
          this.isComponentLoading = false;
        },
      );
  }

  /**
   * Determines state of entity.
   *
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  init() {
    // Is the entity not new? If so, let's get the details.
    if (this.entity.isNew(this.incoming_case_id) === false) {
      this.getLoanDetails(this.incoming_case_id);
    } else {
      // If we're not allowed to have a new reference, move.
      if (this.entity.allowNew(this.stage.current_stage.reference) === false) {
        this.stage_changing.emit('tad_stage_info');
      }
      this.setLoanDetailParams();
    }
  }

  /**
   * Creates an instance of TogetherAuctionDIPLoanDetailComponent.
   * @param {FormBuilder} fb
   * @param {NodeAPIService} api
   * @memberof TogetherAuctionDIPLoanDetailComponent
   */
  constructor(private fb: UntypedFormBuilder,
    private api: NodeAPIService,
    private entity: NodeAPIEntityService,
    private state: DipStateService,
    private exception: ExceptionService) {
    this.tadLoanDetailsForm = fb.group({
      tadPropertyValue: this.tadPropertyValue,
      tadLoanAmount: this.tadLoanAmount,
    }, { validator: this.loanValueValid('tadLoanAmount', 'tadPropertyValue') });
  }

}
